"use client"

import React, { useState } from "react"
import { Alert, Badge, Button, Card, Modal, Spinner } from "react-bootstrap"
import { useFetchPolicies } from "../../hooks/DataFetching/useFetchPolicies"
import "../../styles/components/GroupPolicies.css"

const GroupPolicies = ({ groupId }) => {
  const { policies = [], loading } = useFetchPolicies(groupId)
  const [showModal, setShowModal] = useState(false)
  const [selectedPolicy, setSelectedPolicy] = useState(null)

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return "Invalid Date"
    }
  }

  const handleViewPolicy = (policy) => {
    setSelectedPolicy(policy)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedPolicy(null)
  }

  if (loading) {
    return (
      <div className="tyk-panel d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="info" />
        <span className="ms-3 text-slate-200">Loading policies...</span>
      </div>
    )
  }

  if (!policies.length) {
    return (
      <Alert variant="dark" className="my-4 tyk-panel border-0 text-white">
        No policies have been published for this group yet.
      </Alert>
    )
  }

  return (
    <div className="policies-container py-4">
      <div className="tyk-toolbar">
        <div>
          <span className="tyk-kicker">Coordination library</span>
          <h2 className="m-0 fw-bold text-white">Group Policies</h2>
        </div>
        <Button variant="dark" className="tyk-action">
          <i className="fas fa-plus me-2" /> Create New Policy
        </Button>
      </div>

      <p className="mb-4 text-slate-300">
        Review active group policy, effective dates, and the campaign context behind each decision.
      </p>

      <div className="tyk-grid">
        {policies.map((policy) => (
          <Card className="tyk-card h-100 border-0 text-white" key={policy.id}>
            <Card.Header className="d-flex justify-content-between align-items-start border-bottom border-secondary bg-transparent p-3">
              <div className="me-3">
                <Card.Title className="h5 mb-1">
                  {policy.title || policy.policyStatement || "Untitled Policy"}
                </Card.Title>
                <Card.Subtitle className="text-slate-400 small">
                  {policy.policyStatement
                    ? policy.policyStatement.length > 75
                      ? `${policy.policyStatement.substring(0, 75)}...`
                      : policy.policyStatement
                    : "No statement available."}
                </Card.Subtitle>
              </div>
              <Badge pill bg={policy.policyStatus === "Active" ? "info" : "secondary"} text="dark">
                {policy.policyStatus || "Unknown"}
              </Badge>
            </Card.Header>

            <Card.Body className="p-3">
              <p className="mb-2 small">
                <strong>Campaign:</strong> {policy.campaignId ?? "N/A"}
              </p>
              <p className="mb-2 small">
                <strong>Effective:</strong> {formatDate(policy.effectiveDate)}
              </p>
              <p className="mb-2 small">
                <strong>Created:</strong> {formatDate(policy.createdOn)}
              </p>
            </Card.Body>

            <Card.Footer className="border-top border-secondary bg-transparent p-3">
              <Button
                variant="light"
                size="sm"
                className="tyk-secondary-action w-100"
                onClick={() => handleViewPolicy(policy)}
              >
                <i className="fas fa-eye me-2" /> View Details
              </Button>
            </Card.Footer>
          </Card>
        ))}
      </div>

      <Modal show={showModal} onHide={handleCloseModal} size="lg" className="tyk-modal" centered>
        <Modal.Header closeButton closeVariant="white">
          <div>
            <span className="tyk-kicker">Policy detail</span>
            <Modal.Title className="d-flex align-items-center">
              <span className="me-2">
                {selectedPolicy?.title || selectedPolicy?.policyStatement || "Policy Detail"}
              </span>
              {selectedPolicy?.policyStatus && (
                <Badge bg="info" text="dark" className="ms-2">
                  {selectedPolicy.policyStatus}
                </Badge>
              )}
            </Modal.Title>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="tyk-panel">
            <h5 className="mb-3">Full Statement</h5>
            <p style={{ whiteSpace: "pre-line" }} className="mb-4 text-slate-200">
              {selectedPolicy?.policyStatement ?? "No statement available."}
            </p>

            <div className="border-top border-secondary pt-3 small text-slate-300 d-flex flex-wrap justify-content-between gap-3">
              <div>
                <p className="mb-1">
                  <strong>ID:</strong> {selectedPolicy?.id}
                </p>
                <p className="mb-1">
                  <strong>Group:</strong> {selectedPolicy?.groupId}
                </p>
              </div>
              <div>
                <p className="mb-1">
                  <strong>Effective:</strong> {formatDate(selectedPolicy?.effectiveDate)}
                </p>
                <p className="mb-1">
                  <strong>Created:</strong> {formatDate(selectedPolicy?.createdOn)}
                </p>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button variant="light">
            <i className="fas fa-print me-2" /> Print
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default GroupPolicies
